import { RegisterItem } from '@/drive/interface'
import {
  variableBatchIncrementLimit,
  variableBatchLimit
} from '@/shared/DefaultValues'
import {
  bitIndexDataType,
  dataTypeItems,
  dataTypeRange,
  deadZoneDataType,
  decimalDataType,
  getOptions,
  noneUnitDataType,
  stringDataEncodingItems,
  stringDataType,
  transformDataType,
  variableDataType
} from '@/shared/SelectData'
import {
  PlcItem,
  TemplateVariableEditProps,
  VariableItem
} from '@/template/interface'
import { TemplateReference } from '@/template/reference'
import { getRefTypeDesc } from '@/template/reference/util'
import { getAddressFormat, getMaxDigits } from '@/utils/dataType'
import { Guid } from '@/utils/guid'
import { getFormatMessage } from '@/utils/utils'
import {
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select
} from 'antd'
import React, { Fragment, useEffect, useState } from 'react'

export const TemplateVariableEdit: React.FC<TemplateVariableEditProps> = (
  props: TemplateVariableEditProps
) => {
  const [deviceId, setDeviceId] = useState<string>()
  const [plc, setPlc] = useState<PlcItem>()
  const [dataType, setDataType] = useState<number>(0)
  const [registers, setRegisters] = useState<RegisterItem[]>([])
  const [reg, setReg] = useState<RegisterItem>()
  const [encoding, setEncoding] = useState<number>(1)
  const [enabledIndex, setEnabledIndex] = useState<boolean>(false)
  const [format, setFormat] = useState<string>('')
  const [enableTransform, setEnableTransform] = useState<number>(0)
  const [form] = Form.useForm<VariableItem>()
  const [referenceVisible, setReferenceVisible] = useState<boolean>(false)
  const formatMessage = getFormatMessage()
  const disabled = props.references && props.references.length > 0
  const disableName =
    props.references &&
    props.references.findIndex((x) => x.objectType === 3) >= 0

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 8 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 16 }
    }
  }

  const labelCol = {
    xs: { span: 12 },
    sm: { span: 4 }
  }

  const updateFormat = (reg, dataType) => {
    if (reg) {
      var ret = ''
      var maxMainAddress = reg.maxAddress
      var minMainAddress = reg.minAddress

      var maxSubAdderss = 0
      var minSubAdderss = 0
      var hasSubAddress = false

      if (reg.subType != 0) {
        hasSubAddress = true
      }

      const dt = variableDataType[dataType]
      ret += getAddressFormat(reg.maxAddress, reg.mainType)
      if (hasSubAddress) {
        ret += '.'
        if (!enabledIndex) {
          maxSubAdderss = Math.pow(2, reg.subAddressLength || 0) - 1
          ret += getAddressFormat(maxSubAdderss, reg.subType)
        }
      } else if (enabledIndex) {
        ret += '.'
        var addr = 0
        if (dt.width == 2) {
          addr = 15
        } else if (dt.width == 4) {
          addr = 31
        }
        ret += getAddressFormat(addr, 10)
      }
      ret += '('
      ret += minMainAddress
      if (hasSubAddress || enabledIndex) {
        ret += '.'
        ret += minSubAdderss
      }
      ret += '~'

      if (reg.mainType == 8) {
        ret += maxMainAddress?.toString(8).toUpperCase()
      } else if (reg.mainType == 16) {
        ret += maxMainAddress?.toString(16).toUpperCase()
      } else {
        ret += maxMainAddress
      }

      if (hasSubAddress) {
        ret += '.'
        if (!enabledIndex) {
          if (reg.subType == 8) {
            ret += maxSubAdderss.toString(8).toUpperCase()
          } else if (reg.subType == 16) {
            ret += maxSubAdderss.toString(16).toUpperCase()
          } else {
            ret += maxSubAdderss
          }
        }
      } else if (enabledIndex) {
        ret += '.'
        if (dt.width == 2) {
          ret += 15
        } else if (dt.width == 4) {
          ret += 31
        }
      }
      ret += ')'
      setFormat(ret)
    }
  }

  const updateTransform = (value, dt) => {
    const item: VariableItem = form.getFieldsValue()
    if (!item.valueTransform) {
      item.valueTransform = { type: value }
    }

    if (value == 0) {
      item.valueTransform.scale = undefined
    }

    if (value == 1) {
      item.valueTransform.scale = {
        maxValue: dataTypeRange[dt].maxValueLimit,
        minValue: dataTypeRange[dt].minValueLimit,
        scaleMaxValue: 100,
        scaleMinValue: 0
      }
    }

    if (value == 2) {
      item.valueTransform.zoom = {
        gain: 1,
        offset: 0
      }
    }

    form.setFieldsValue(item)
  }

  const updateRegister = (deviceId, dataType, regName?: string) => {
    const plc = props.plcs?.find((x) => x.id == deviceId)
    setPlc(plc)
    if (plc && plc.registers) {
      const dt = variableDataType[dataType]
      const regs = plc.registers.filter((x) => {
        if (dt.width >= x.addressWidth) {
          if (dt.width == 0) return true
          if (x.addressWidth != 0) return true
        }
        return false
      })
      setRegisters(regs)
      if (regs && regs.length > 0) {
        let selectReg = regName
          ? regs.find((x) => x.name == regName) || regs[0]
          : regs[0]
        const item: VariableItem = form.getFieldsValue()
        item.registerId = selectReg.id
        item.registerName = selectReg.name
        if (!item.stationNo) {
          item.stationNo = plc.type == 'local' ? undefined : 1
        }
        setReg(selectReg)
        updateFormat(selectReg, dataType)
        // updateDescription(selectReg.name, item.mainAddress, item)
        form.setFieldsValue(item)
      }
    }
  }

  // const updateDescription = (
  //   regName?: string,
  //   mainAddr?: number,
  //   item?: VariableItem
  // ) => {
  //   if (item) {
  //     item.description = `${regName || item.registerName}${
  //       mainAddr || item.mainAddress
  //     }`
  //   } else {
  //     const item: VariableItem = form.getFieldsValue()
  //     item.description = `${regName || item.registerName}${
  //       mainAddr || item.mainAddress
  //     }`
  //     form.setFieldsValue(item)
  //   }
  // }

  const handleDeviceChange = (value) => {
    setDeviceId(value)
    updateRegister(value, dataType)
  }

  const handleDataTypeChange = (value) => {
    setDataType(value)
    updateRegister(deviceId, value)
    updateTransform(enableTransform, value)
    // 取消按位索引
    setEnabledIndex(false)
    form.setFieldsValue({ enabledIndex: false })
  }

  const handleRegisterChange = (value) => {
    const exist = registers.find((x) => x.name == value)
    if (exist) {
      setReg(exist)
      // updateDescription(exist.name)
    }
  }

  const hanldeEnableTransform = (value: number) => {
    setEnableTransform(value)
    updateTransform(value, dataType)
  }

  useEffect(() => {
    updateFormat(reg, dataType)
  }, [enabledIndex])

  useEffect(() => {
    form.setFieldsValue(props.item || {})
    setDeviceId(props.item?.deviceId)
    setDataType(props.item?.dataType || 0)
    setEnableTransform(props.item?.valueTransform?.type || 0)
    setEnabledIndex(props.item?.enabledIndex || false)
    setEncoding(props.item?.stringSetting?.encoding || 1)
    if (props.plcs) {
      updateRegister(
        props.item?.deviceId,
        props.item?.dataType,
        props.item?.registerName
      )
    }
  }, [props.item])

  useEffect(() => {
    if (props.formResult) {
      form
        .validateFields()
        .then((values: VariableItem) => {
          const merged = Object.assign(props.item, values)
          // 未启用按位索引时，都置为false
          if (!enabledIndex) {
            merged.enabledIndex = false
          } else {
            // 重置整数位、小数位、边缘换算、死区、单位(jira-FBOX-3377)
            merged.integerDigits = 4
            merged.fractionDigits = 0
            merged.valueTransform = { type: 0 }
            merged.deadZone = 0
            delete merged.unit
          }

          if (!enableTransform && merged.valueTransform) {
            merged.valueTransform.type = 0
          }

          // 如果没有添加备注，则自动添加备注信息
          if (!merged.description) {
            merged.description = `${merged.registerName}${merged.mainAddress}`
          }

          if (props.onFormValidate) {
            if (!props.onFormValidate(true, merged)) return
          }
          if (props.onFormSubmit) {
            if (!merged.id) {
              merged.id = Guid.raw()
            }
            props.onFormSubmit(merged)
            form.resetFields()
          }
        })
        .catch((info) => {
          console.log('Validate Failed:', info)
          if (props.onFormValidate) {
            props.onFormValidate(false)
          }
        })
    }
  }, [props.formResult])

  return (
    <Fragment>
      <Form form={form} {...formItemLayout}>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              labelCol={labelCol}
              label={formatMessage({
                id: 'app.template.variable.edit.name'
              })}
              name='name'
              rules={[
                {
                  required: true,
                  transform: (value) => value?.trim(),
                  message: formatMessage({
                    id: 'app.template.variable.edit.name.message'
                  })
                }
              ]}
            >
              <Input disabled={disableName} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.variable.edit.deviceId'
              })}
              name='deviceId'
              rules={[
                {
                  required: true,
                  message: '设备不可为空'
                }
              ]}
            >
              <Select disabled={disabled} onChange={handleDeviceChange}>
                {props.plcs &&
                  props.plcs
                    .filter((x) => x.deviceMode === 0)
                    .map((x) => (
                      <Select.Option key={Guid.raw()} value={x.id || ''}>
                        {x.alias}
                      </Select.Option>
                    ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            {plc?.type != 'local' && (
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.stationNo'
                })}
                name='stationNo'
                rules={[
                  {
                    required: true,
                    message: '站号不可为空'
                  }
                ]}
              >
                <InputNumber disabled={disabled} style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.variable.edit.dataType'
              })}
              name='dataType'
              rules={[
                {
                  required: true,
                  message: '数据类型不可为空'
                }
              ]}
            >
              <Select disabled={disabled} onChange={handleDataTypeChange}>
                {getOptions(dataTypeItems)}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.variable.edit.registerName'
              })}
              name='registerName'
              rules={[
                {
                  required: true,
                  message: formatMessage({
                    id: 'app.template.variable.edit.registerName.message'
                  })
                }
              ]}
            >
              <Select disabled={disabled} onChange={handleRegisterChange}>
                {registers &&
                  registers.map((x) => (
                    <Select.Option key={Guid.raw()} value={x.name || ''}>
                      {x.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {bitIndexDataType.includes(dataType) && (
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                labelCol={labelCol}
                label={formatMessage({
                  id: 'app.template.variable.edit.enabledIndex'
                })}
                name='enabledIndex'
                valuePropName='checked'
              >
                <Checkbox
                  disabled={disabled}
                  onChange={(e) => setEnabledIndex(e.target.checked)}
                >
                  {formatMessage({
                    id: 'app.template.variable.edit.enabledIndex.desc'
                  })}
                  <span style={{ color: 'red' }}>
                    {formatMessage({
                      id: 'app.template.variable.edit.enabledIndex.macroDesc'
                    })}
                  </span>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        )}

        {reg?.hasSubIndex && (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={'地址块索引'}
                name='subIndex'
                rules={[
                  {
                    required: true,
                    message: '地址块索引不可为空'
                  }
                ]}
              >
                <InputNumber
                  disabled={disabled}
                  min={0}
                  max={65535}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}></Col>
          </Row>
        )}

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.variable.edit.mainAddress'
              })}
              name='mainAddress'
              rules={[
                {
                  required: true,
                  message: '地址不可为空'
                }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                // onBlur={(e) => updateDescription(undefined, +e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            {reg?.hasSubAddress && (
              <Form.Item
                label='子地址'
                name='subAddress'
                rules={[
                  {
                    required: true,
                    message: '子地址不可为空'
                  }
                ]}
              >
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            )}

            {bitIndexDataType.includes(dataType) && enabledIndex && (
              <Form.Item label='位索引' name='bitIndex'>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            )}
          </Col>
        </Row>

        {decimalDataType.includes(dataType) && !enabledIndex && (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.integerDigits'
                })}
                name='integerDigits'
                rules={[
                  {
                    required: true,
                    message: '整数位不可为空'
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={getMaxDigits(dataType)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.fractionDigits'
                })}
                name='fractionDigits'
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'app.template.variable.edit.fractionDigits.message'
                    })
                  }
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={getMaxDigits(dataType)}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {stringDataType.includes(dataType) && (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.encoding'
                })}
                name={['stringSetting', 'encoding']}
              >
                <Select onChange={(value) => setEncoding(value as number)}>
                  {getOptions(stringDataEncodingItems)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {encoding == 2 && (
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.variable.edit.order'
                  })}
                  name={['stringSetting', 'order']}
                  valuePropName='checked'
                >
                  <Checkbox />
                </Form.Item>
              )}
            </Col>
          </Row>
        )}

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={formatMessage({
                id: 'app.template.variable.edit.format'
              })}
            >
              <label>{format}</label>
            </Form.Item>
          </Col>
          <Col span={12}>
            {!noneUnitDataType.includes(dataType) && !enabledIndex && (
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.unit'
                })}
                name='unit'
              >
                <Input />
              </Form.Item>
            )}

            {stringDataType.includes(dataType) && (
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.charCount'
                })}
                name={['stringSetting', 'charCount']}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={99} />
              </Form.Item>
            )}
          </Col>
        </Row>

        {props.batch && (
          <Fragment>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.variable.edit.batch'
                  })}
                  name={['batch', 'type']}
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.template.variable.edit.batch.message'
                      })
                    }
                  ]}
                >
                  <Select>
                    <Select.Option value={0}>主地址</Select.Option>
                    {reg?.hasSubAddress && (
                      <Select.Option value={1}>子地址</Select.Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.variable.edit.batch.increment'
                  })}
                  name={['batch', 'increment']}
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.template.variable.edit.batch.increment.message'
                      })
                    }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={variableBatchIncrementLimit[0]}
                    max={variableBatchIncrementLimit[1]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.variable.edit.batch.count'
                  })}
                  name={['batch', 'count']}
                  rules={[
                    {
                      required: true,
                      message: formatMessage({
                        id: 'app.template.variable.edit.batch.count.message'
                      })
                    }
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={variableBatchLimit[0]}
                    max={variableBatchLimit[1]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}></Col>
            </Row>
          </Fragment>
        )}

        {transformDataType.includes(dataType) && !enabledIndex && (
          <Fragment>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label={formatMessage({
                    id: 'app.template.variable.edit.valueTransform'
                  })}
                  labelCol={labelCol}
                  name={['valueTransform', 'type']}
                >
                  <Radio.Group
                    onChange={(e) => hanldeEnableTransform(e.target.value)}
                  >
                    <Radio value={0}>
                      {formatMessage({
                        id: 'app.template.variable.edit.valueTransform.0'
                      })}
                    </Radio>
                    <Radio value={1}>
                      {formatMessage({
                        id: 'app.template.variable.edit.valueTransform.1'
                      })}
                    </Radio>
                    <Radio value={2}>
                      {formatMessage({
                        id: 'app.template.variable.edit.valueTransform.2'
                      })}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            {enableTransform == 1 && (
              <Fragment>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.variable.edit.valueTransform.maxValue'
                      })}
                      name={['valueTransform', 'scale', 'maxValue']}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.variable.edit.valueTransform.minValue'
                      })}
                      name={['valueTransform', 'scale', 'minValue']}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.variable.edit.valueTransform.scaleMaxValue'
                      })}
                      name={['valueTransform', 'scale', 'scaleMaxValue']}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label={formatMessage({
                        id: 'app.template.variable.edit.valueTransform.scaleMinValue'
                      })}
                      name={['valueTransform', 'scale', 'scaleMinValue']}
                    >
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Fragment>
            )}

            {enableTransform == 2 && (
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item
                    label={formatMessage({
                      id: 'app.template.variable.edit.valueTransform.gain'
                    })}
                    name={['valueTransform', 'zoom', 'gain']}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label={formatMessage({
                      id: 'app.template.variable.edit.valueTransform.offset'
                    })}
                    name={['valueTransform', 'zoom', 'offset']}
                  >
                    <InputNumber style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Fragment>
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              labelCol={labelCol}
              label={formatMessage({
                id: 'app.template.variable.edit.privilege'
              })}
              name='privilege'
            >
              <Radio.Group>
                <Radio value={4}>
                  {formatMessage({ id: 'app.privilege.4' })}
                </Radio>
                <Radio value={6}>
                  {formatMessage({ id: 'app.privilege.6' })}
                </Radio>
                {plc?.type != 'local' && (
                  <Radio value={2}>
                    {formatMessage({ id: 'app.privilege.2' })}
                  </Radio>
                )}
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {deadZoneDataType.includes(dataType) && !enabledIndex && (
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={formatMessage({
                  id: 'app.template.variable.edit.deadZone'
                })}
                name='deadZone'
              >
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={12}></Col>
          </Row>
        )}

        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              labelCol={labelCol}
              label={formatMessage({
                id: 'app.template.variable.edit.description'
              })}
              name='description'
            >
              <Input.TextArea />
            </Form.Item>
          </Col>
        </Row>

        {disabled && (
          <Row gutter={24}>
            <Col span={24}>
              <span style={{ color: 'red' }}>
                {formatMessage({
                  id: 'app.template.variable.edit.references',
                  parameters: { refs: getRefTypeDesc(props.references!) }
                })}
              </span>
              {disableName && (
                <span style={{ color: 'red' }}>
                  {formatMessage({
                    id: 'app.template.variable.edit.references.macro'
                  })}
                </span>
              )}
              <a
                onClick={() => {
                  setReferenceVisible(true)
                }}
              >
                {formatMessage({ id: 'app.template.reference.view' })}
              </a>
            </Col>
          </Row>
        )}
      </Form>
      <TemplateReference
        data={props.references}
        visible={referenceVisible}
        onVisibleChange={setReferenceVisible}
        onRefRelease={props.onRefRelease}
      />
    </Fragment>
  )
}
